import { jsPDF } from 'jspdf';
import { money } from './format';
import {
  PDF_BRAND,
  drawCard,
  drawLabelValue,
  drawPdfHeader,
  drawSectionTitle,
  ensureSpace,
  finalizePdfFooters,
  pageMetrics,
} from './pdfTheme';

function qtyOf(value) {
  const n = parseInt(value, 10);
  if (Number.isNaN(n) || n < 1) return 1;
  return n;
}

function statusCopy(status) {
  if (status === 'sent') return 'Sent';
  if (status === 'accepted') return 'Accepted';
  if (status === 'rejected') return 'Rejected';
  if (status === 'partial') return 'Partial';
  if (status === 'draft') return 'Draft';
  return status || 'Sent';
}

export function quoteFileName(bookingId, quoteKind) {
  const suffix = quoteKind && quoteKind !== 'repair' ? `-${quoteKind}` : '';
  return `quotation-${bookingId}${suffix}.pdf`;
}

export async function buildQuotePdf(quote, booking, business, options = {}) {
  const quoteKind = options.quoteKind || quote?.quote_kind || 'repair';
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const margin = 40;
  const { contentWidth, pageHeight } = pageMetrics(doc, margin);

  const titleByKind = {
    inspection: 'Pre-sale inspection quotation',
    repair: 'Repair quotation',
  };
  const docTitle = options.title || titleByKind[quoteKind] || 'Quotation';
  const status = statusCopy(quote?.status || 'sent');
  const whenRaw = booking?.scheduled_at || booking?.created_at;
  const when = whenRaw ? new Date(whenRaw).toLocaleString() : '—';
  const issued = new Date().toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  const customerName =
    booking?.profiles?.full_name || booking?.profiles?.first_name || '—';
  const customerPhone = booking?.profiles?.phone || '—';
  const customerEmail = booking?.profiles?.email || '—';
  const branchLine = `${booking?.branches?.name || '—'}${
    booking?.branches?.address ? ` · ${booking.branches.address}` : ''
  }${booking?.branches?.phone ? ` · ${booking.branches.phone}` : ''}`;

  let y = await drawPdfHeader(doc, business, {
    margin,
    subtitle: business?.tagline || 'Service quotation',
    badge: `Status · ${status}`,
  });

  y = drawSectionTitle(doc, docTitle, margin, y, contentWidth);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  doc.setTextColor(...PDF_BRAND.muted);
  doc.text(`Issued ${issued}`, margin + contentWidth, y - 10, { align: 'right' });
  y += 6;

  const colGap = 12;
  const colWidth = (contentWidth - colGap) / 2;
  drawCard(doc, margin, y, colWidth, 96);
  let cardY = y + 16;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...PDF_BRAND.ink);
  doc.text('Visit', margin + 12, cardY);
  cardY += 18;
  cardY = drawLabelValue(
    doc,
    'Service',
    booking?.service_type || 'Visit',
    margin + 12,
    cardY,
    colWidth - 24,
  );
  cardY = drawLabelValue(doc, 'Scheduled', when, margin + 12, cardY, colWidth - 24);
  drawLabelValue(doc, 'Branch', branchLine, margin + 12, cardY, colWidth - 24);

  const customerX = margin + colWidth + colGap;
  drawCard(doc, customerX, y, colWidth, 96);
  cardY = y + 16;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('Customer', customerX + 12, cardY);
  cardY += 18;
  cardY = drawLabelValue(doc, 'Name', customerName, customerX + 12, cardY, colWidth - 24);
  cardY = drawLabelValue(doc, 'Phone', customerPhone, customerX + 12, cardY, colWidth - 24);
  drawLabelValue(doc, 'Email', customerEmail, customerX + 12, cardY, colWidth - 24);

  y += 112;

  doc.setFillColor(...PDF_BRAND.ink);
  doc.roundedRect(margin, y, contentWidth, 26, 6, 6, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9.5);
  doc.text('Item', margin + 10, y + 16);
  doc.text('Qty × Unit', margin + contentWidth - 210, y + 16);
  doc.text('Line total', margin + contentWidth - 10, y + 16, { align: 'right' });
  y += 32;

  let total = 0;
  (quote?.items || []).forEach((item, idx) => {
    const itemTotal = Number(item.price || 0);
    total += itemTotal;
    const qty = qtyOf(item.quantity);
    const unit = Number(item.unit_price || 0);
    const rowH = 34;

    y = ensureSpace(doc, y, rowH + 8, margin, () => margin + 20);
    doc.setFillColor(...(idx % 2 === 0 ? PDF_BRAND.panel : PDF_BRAND.soft));
    doc.roundedRect(margin, y, contentWidth, rowH, 4, 4, 'F');

    doc.setTextColor(...PDF_BRAND.ink);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);
    doc.text(item.description || '—', margin + 10, y + 14, { maxWidth: contentWidth - 240 });

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...PDF_BRAND.muted);
    doc.setFontSize(9);
    doc.text(`${qty} × ${money(unit, 2)}`, margin + contentWidth - 210, y + 14);

    doc.setTextColor(...PDF_BRAND.ink);
    doc.setFont('helvetica', 'bold');
    doc.text(money(itemTotal, 2), margin + contentWidth - 10, y + 14, { align: 'right' });

    y += rowH + 6;
  });

  y = ensureSpace(doc, y, 48, margin, () => margin + 20);
  doc.setFillColor(...PDF_BRAND.accent);
  doc.roundedRect(margin, y, contentWidth, 36, 8, 8, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.text('TOTAL', margin + 12, y + 23);
  doc.text(money(total, 2), margin + contentWidth - 12, y + 23, { align: 'right' });
  y += 48;

  if (quote?.note) {
    y = ensureSpace(doc, y, 70, margin, () => margin + 20);
    y = drawSectionTitle(doc, 'Note to customer', margin, y, contentWidth);
    drawCard(doc, margin, y, contentWidth, 56);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    doc.setTextColor(...PDF_BRAND.muted);
    const wrapped = doc.splitTextToSize(quote.note, contentWidth - 24);
    doc.text(wrapped, margin + 12, y + 18);
    y += 68;
  }

  if (y < pageHeight - 100) {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8);
    doc.setTextColor(...PDF_BRAND.faint);
    doc.text(
      'Prices are estimates unless otherwise agreed in writing. Validity and availability may change after issue.',
      margin,
      pageHeight - 52,
      { maxWidth: contentWidth },
    );
  }

  finalizePdfFooters(doc, business, margin);
  return doc;
}
